import { Star, User, DollarSign, UtensilsCrossed, Activity, Home, MapPin, Globe } from 'lucide-react';

const TripReviews = ({ reviews }) => {
  if (!reviews || reviews.length === 0) return null;

  const calculateAverageRating = () => {
    if (!reviews || reviews.length === 0) return 0;
    const totalRatings = reviews.reduce((sum, review) => {
      const overallRating = review.ratings?.overallImpression || 0;
      return sum + overallRating;
    }, 0);
    return (totalRatings / reviews.length).toFixed(1);
  };

  const averageRating = calculateAverageRating();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const ratingLabels = {
    valueForMoney: { label: 'Value for money', icon: DollarSign },
    food: { label: 'Food', icon: UtensilsCrossed },
    qualityOfActivity: { label: 'Quality of activity', icon: Activity },
    accommodationFacilities: { label: 'Accommodation & facilities', icon: Home },
    location: { label: 'Location', icon: MapPin },
    overallImpression: { label: 'Overall impression', icon: Globe },
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-medium text-gray-900 flex items-center gap-2">
            <Star className="w-6 h-6 text-emerald-600 fill-emerald-600" />
            <span>Guest Reviews</span>
          </h2>
          {averageRating > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-2xl font-semibold text-gray-900">{averageRating}</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= Math.round(averageRating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600 ml-2">({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-6">
        {reviews.map((review) => (
          <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{review.reviewerName}</p>
                  {review.reviewerLocation && (
                    <p className="text-sm text-gray-500">from {review.reviewerLocation}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{formatDate(review.date)}</p>
              </div>
            </div>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{review.title}</h4>
            <p className="text-gray-700 leading-relaxed mb-4">{review.content}</p>
            
            {/* All Ratings */}
            {review.ratings && (
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                {Object.entries(review.ratings).map(([key, value]) => {
                  const ratingInfo = ratingLabels[key];
                  if (!ratingInfo) return null;
                  const RatingIcon = ratingInfo.icon;
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RatingIcon className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">{ratingInfo.label}:</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= value
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Host Response */}
            {review.hostResponse && (
              <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">Organizer response:</p>
                <p className="text-sm text-gray-700 mb-1">{review.hostResponse}</p>
                {review.hostResponseDate && (
                  <p className="text-xs text-gray-500">on {formatDate(review.hostResponseDate)}</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TripReviews;

